import { assignBlockIds, createSeededBlockIdGenerator } from "./block-id";
import {
  CURRENT_SCHEMA_VERSION,
  DOCUMENT_FORMAT,
  type DocumentId,
  type EditorDocument,
  type SerializedEditorState,
  type SerializedNodeLike,
} from "./types";

/** Used when a document is created without an editor instance; the editor stamps its own on save. */
export const UNSPECIFIED_ENGINE_VERSION = "unspecified";

export function createEmptyContent(): SerializedEditorState {
  return {
    root: {
      type: "root",
      version: 1,
      direction: null,
      format: "",
      indent: 0,
      children: [
        {
          type: "paragraph",
          version: 1,
          direction: null,
          format: "",
          indent: 0,
          textFormat: 0,
          textStyle: "",
          children: [],
        } as SerializedEditorState["root"]["children"][number],
      ],
    },
  };
}

export function createDocument(
  documentId: DocumentId,
  content: SerializedEditorState,
  engineVersion: string = UNSPECIFIED_ENGINE_VERSION,
): EditorDocument {
  return {
    format: DOCUMENT_FORMAT,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    documentId,
    engine: { name: "lexical", version: engineVersion },
    content,
  };
}

/** An empty document whose one paragraph is already identified, the same way for every caller. */
export function createEmptyDocument(
  documentId: DocumentId,
  engineVersion: string = UNSPECIFIED_ENGINE_VERSION,
): EditorDocument {
  const { root } = assignBlockIds(
    createEmptyContent().root as SerializedNodeLike,
    createSeededBlockIdGenerator(documentId),
  );
  return createDocument(documentId, { root } as SerializedEditorState, engineVersion);
}

/** Whether the content is a single empty paragraph (or has no blocks at all). */
export function isEmptyContent(content: SerializedEditorState): boolean {
  const blocks = content.root.children;
  if (blocks.length === 0) return true;
  if (blocks.length > 1) return false;
  const only = blocks[0] as { type: string; children?: unknown[] };
  return only.type === "paragraph" && (only.children?.length ?? 0) === 0;
}
